/**
 * パスワード再設定の完了（外したログイン方法・残っているサーバーの鍵）と、サーバーの鍵の一覧・まとめて作り直す画面の英語の文言
 * （2026-09-28 creator/dashboard-pass・Core SEC-PKLOGIN-01 の続き）。en.ts が `...enSecretKeys` で取り込む（ja.secretKeys.ts が同じキーを全部持つことを型で強制）。
 * 使う画面: pages/ResetDone.tsx・pages/AccountSecretKeys.tsx・pages/AccountPage.tsx（入口の欄）。
 *
 * 文言の正（指示書・Core と合意）:
 *   外したログイン方法 =「安全のため、次のログイン方法を外しました。心当たりのある物は、ログインしてからアカウント設定で付け直してください
 *     （見覚えのない日時の物は付け直さない）」
 *   残っている鍵 =「確認の後に作られたサーバーの鍵です。見覚えのない鍵があれば、まとめて作り直してください」
 *   → 2026-10-04 founder「バイブコーダーに意味が通じない」で resetDone.* を管理が直し、plain-words-4 で一覧・作り直しの画面（skList.* / rotate.*）も
 *     同じ言い方にした:「サーバーの鍵」→「サーバーだけで使うキー（sk_）」、失効 / rotate →「使えなくする」「作り直す（replace）」。
 *     見張りは plainWords.test.ts の INNER_WORDS。
 * 利用者が付けた名前（label・プロジェクト名）は文言に差し込まず、画面が別の要素（テキスト）として出す。
 */
export const enSecretKeys = {
  // ------------------------------------------------------------ 再設定の完了（/reset-password/done）
  'resetDone.title': 'Password changed',
  'resetDone.lead': 'Just in case, check here that nobody but you got into your account.',
  'resetDone.removedTitle': 'Sign-in methods we removed to keep you safe',
  'resetDone.removedBody':
    'For safety, we removed the sign-in methods below. If you recognize one, add it again from account settings after you log in. Do not add back anything added at a time you do not recognize.',
  'resetDone.passkey': 'Sign-in passkey',
  'resetDone.provider': 'Sign in with {provider}',
  'resetDone.added': 'Added {date}',
  'resetDone.lastUsed': 'Last used {date}',
  'resetDone.neverUsed': 'Never used',
  'resetDone.keysTitle': 'Server-only keys (starting with sk_) that still work',
  'resetDone.keysBody': 'If you see a key that you (or your AI) did not make, someone else might be able to change your app. If so, use the button below to replace all keys with new ones. The old keys stop working, so then ask your AI to "switch to the new key".',
  'resetDone.rotate': 'Log in and replace all keys',
  'resetDone.rotateNote': 'Before anything is replaced, we check once more that it is you.',
  'resetDone.login': 'I recognize all of these, log in',

  // ------------------------------------------------------------ 鍵の行（共通）
  'sk.col.key': 'Key',
  'sk.col.env': 'App',
  'sk.col.via': 'Created by',
  'sk.col.created': 'Created',
  'sk.col.lastUsed': 'Last used',
  'sk.noLabel': 'No name',
  'sk.functionsPill': 'Functions',
  'sk.via.dashboard': 'Dashboard',
  'sk.via.cli': 'Terminal command',
  'sk.via.agent': 'AI',
  'sk.via.functions': 'Automatic (Functions)',
  'sk.via.rotate_all': 'Replace all',
  'sk.via.unknown': 'Unknown',

  // ------------------------------------------------------------ 一覧（/account/secret-keys）
  'skList.eyebrow': 'Account',
  'skList.title': 'Server-only keys (sk_)',
  'skList.lead':
    'Every server-only key (starting with sk_) that still works, across all your projects. The key itself (the long text starting with sk_) is shown only once, when it is made, so it is not shown here. To turn off or add one key, use the “Keys (pk_ / sk_)” page of its project.',
  'skList.openKeys': 'Keys (pk_ / sk_)',
  'skList.empty': 'No server-only keys (sk_) that still work.',
  'skList.rotateAll': 'Replace all keys…',
  'skList.rotateHint': 'If you see a key that you (or your AI) did not make, someone else might be able to change your app. If so, replace them all.',
  'skList.cardTitle': 'Server-only keys (sk_)',
  'skList.cardBody': 'See every server-only key (sk_) across your projects, and replace them all at once if one looks unfamiliar.',
  'skList.cardLink': 'Open the key list',

  // ------------------------------------------------------------ まとめて作り直す（/account/secret-keys/rotate）
  'rotate.title': 'Replace all server-only keys',
  'rotate.lead':
    'Every key below stops working and is replaced by a new key for the same project, the same app (live or test) and the same name. A server that still has an old key stops working until you put in the new one. This cannot be undone.',
  'rotate.functionsRow': 'Republish Functions',
  'rotate.functionsNote':
    'Rows marked “Republish Functions” are keys your Functions (the server part of your app) hold on their own. Their new key is not shown here: it goes in by itself the next time your Functions are published. Until then, your Functions cannot use {product} features (sign-in, saves and so on).',
  'rotate.confirm': { one: 'Replace {count} key', other: 'Replace {count} keys' },
  'rotate.busy': 'Replacing…',
  'rotate.resumed': 'We checked that it is you. Look at the list once more, then press the button to replace the keys.',
  'rotate.changed': 'Keys were added or removed after this list was shown. This is the list now — look at it again before replacing.',
  'rotate.none': 'You have no server-only keys that still work, so there is nothing to replace.',
  'rotate.back': 'Back to the key list',
  'rotate.doneTitle': 'New keys created',
  'rotate.doneWarn': 'You can see the new keys only on this page — once you leave, they can never be shown again. Copy each one now and put it where the old key was (your server settings). Do not paste the keys themselves into a conversation with your AI.',
  'rotate.doneRevoked': { one: '{count} old key no longer works.', other: '{count} old keys no longer work.' },
  'rotate.replaces': 'Replaces {key}',
  'rotate.redeployTitle': 'Republish Functions',
  'rotate.redeployBody': 'The keys your Functions held on their own no longer work. Until they are published again, those Functions cannot use {product}. Ask your AI to “republish Functions”. If you type it yourself, run this in each project’s folder:',
  'rotate.finish': 'I saved the new keys',

  'error.keys_changed': 'Keys were added or removed after the list was shown. Look at the list again before replacing them.',
  'error.no_secret_keys': 'You have no server-only keys that still work, so there is nothing to replace.'
};
